import React, { useState, useEffect } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import RegistrationForm from './RegistrationForm/RegistrationForm';
import EventPublicInfo from '../LoginWrapper/EventPublicInfo/EventPublicInfo';
import LoginForm from '../LoginWrapper/LoginForm/LoginForm';
import { REGISTRATION_PATH, ROOT } from '../../../config/routes/paths';
import { EventPublicDetailsResponseTypeWithCustomField } from '../../../hooks/api/public/use-event-public-custom-fields';
import { useCustomFieldsByTriageAnswerSettings, useCustomFieldsSettings } from '../../../hooks/use-custom-fields';
import RegisterBlock, { FormFooterBlockType } from '../LoginWrapper/RegisterBlock/RegisterBlock';
import Buttons from '../../../components/button';
import useEventCalendar from '../../../hooks/use-event-calendar';
import { useRegistrationSubscription } from '../../../hooks/use-registration-subscription';
import { useMagicLinkAuth } from '../../../hooks/use-magic-link-auth';
import DefaultSpinner from '../../shared/DefaultSpinner';
import { useEventGeneralSessionList } from '../../../hooks/api/public/use-event-general-session-list';
import EventDetails from '../../shared/EventDetails';
import './RegistrationWrapper.scss';
import { usePathPrefix } from '../../../hooks/use-path-prefix';
import { normalizeRoutePath } from '../../../lib/helpers/urlHelper';
import { PassportNonce, RegistrationStatus } from '../../../lib/constants';
import SafeHTMLRender from '../../shared/SafeHTMLRender';

type Props = {
  signIn(email: string, code: string): Promise<void>;
  data: EventPublicDetailsResponseTypeWithCustomField;
  location: { from?: string, errorMsg?: string; email?: string };
  selectedTriageAnswer: string;
  urlParams: Record<string, string | null>;
};

const getEventTitle = (customTitle: string | undefined, defaultTitle: string): string => {
  return customTitle ? customTitle : defaultTitle;
};

export const shouldAuthenticateRegistrationMagicLink = (
  magicLink: string | null,
  autoSignInEnabled: boolean,
  registrationApprovalEnabled: boolean,
  registrationStatus?: RegistrationStatus
): boolean => {
  if (!magicLink) {
    return false;
  }
  if (autoSignInEnabled && !registrationApprovalEnabled && registrationStatus !== RegistrationStatus.APPROVED) {
    return false;
  }
  return true;
};

const RegistrationWrapper: React.FC<Props> = (props: Props): JSX.Element => {
  const {
    signIn,
    data,
    location,
    selectedTriageAnswer,
    urlParams,
  } = props;

  const history = useHistory();
  const pathPrefix = usePathPrefix();

  const { state, subscribe } = useRegistrationSubscription();
  const { authenticate } = useMagicLinkAuth();
  const subscriptionInProgress = !!(state.inProgress || (state.magicLink && !state.error));
  const subscriptionError = (!state.inProgress && !state.magicLink && state.error) || null;
  const subscriptionCompletedWithoutMagicLink = !state.inProgress && !state.magicLink && !state.error;

  const {
    customFields,
    loading,
    triagedRegistrationEnabled,
    configuration: defaultConfiguration
  } = useCustomFieldsSettings(data.eventId, data.registrationEnabled, selectedTriageAnswer);

  const {
    customFields: triageAnswerCustomFields,
    loading: fetching = false,
    error: triageAnswerCustomFieldsError,
    triagedRegistrationEnabled: triagedEnabled,
    configuration: triageConfiguration
  } = useCustomFieldsByTriageAnswerSettings(data.eventId, selectedTriageAnswer, data.registrationEnabled);

  const registrationConfiguration = selectedTriageAnswer ? triageConfiguration : defaultConfiguration;
  const isProRegistrationView = registrationConfiguration?.showOpenMeetings;
  const formFields = selectedTriageAnswer ? triageAnswerCustomFields : customFields;
  const isTriageQuestionDisabled = triagedRegistrationEnabled === false || triagedEnabled === false;

  const { data: generalSessionList } = useEventGeneralSessionList(data.eventId.toString(), registrationConfiguration?.showOpenMeetings);
  const { fetchOutlookCalendarForEvent, fetchGoogleCalendarForEvent } = useEventCalendar();
  const [showErrorRes, setErrorRes] = useState<boolean>(false);
  const [showRegistrationForm, setRegistrationForm] = useState<boolean>(true);
  const [showSubmitMessage, setShowSubmitMessage] = useState<boolean>(false);
  const [emailText, setEmailText] = useState<string>('');
  const [eventToken, setToken] = useState<string | undefined>(undefined);
  const [showApprovalWaitingMessage, setShowApprovalWaitingMessage] = useState<boolean>(false);
  const [registrationStatus, setRegistrationStatus] = useState<RegistrationStatus | undefined>(undefined);
  const autoSignInEnabled = !!registrationConfiguration?.autoSignInEnabled;

  useEffect(() => {
    const magicLink = state.magicLink;
    if (!shouldAuthenticateRegistrationMagicLink(
      magicLink,
      autoSignInEnabled,
      !!data.registrationApprovalEnabled,
      registrationStatus
    ) || !magicLink) {
      return;
    }
    authenticate(magicLink);
    // authenticate is omitted so a new function identity does not sign the attendee in again
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.magicLink, registrationStatus, autoSignInEnabled, data.registrationApprovalEnabled]);

  const submitMessage = `Thank you for registering.  Please check your email for details of how to attend the event.`;
  const errorMessage = `Sorry, registration parameters were changed. Please refresh the page.`;
  const defaultApprovalWaitingMessage = 'Please wait while we confirm your registration';
  const defaultPendingDeniedMessage = 'We have not been able authenticate you automatically, please check your email for further details or contact the event coordinator';
  const pendingDeniedMessage = registrationConfiguration?.pendingDeniedMessage || defaultPendingDeniedMessage;
  const clName = `shadow-gray mt-6 md:mt-0 min-w-full md:min-w-0 ${isProRegistrationView ? 'bg-white w-full px-10' : 'w-342px'}`;
  const clNameFormWrap = formFields?.length ?
    `registration-wrapper login-wrapper p-6 ${isProRegistrationView ? 'bg-white registration-wrapper--pro' : 'bg-white-opacity-95'} ` :
    'login-wrapper p-6 bg-white ';
  const fileName = `${getEventTitle(data.eventTitleForPassport, data.name)}.ics`;
  const showSubscriptionError = subscriptionError && !showErrorRes;

  useEffect(() => {
    if (showApprovalWaitingMessage && (!subscriptionInProgress || subscriptionError || subscriptionCompletedWithoutMagicLink)) {
      setShowApprovalWaitingMessage(false);
    }
  }, [showApprovalWaitingMessage, subscriptionInProgress, subscriptionError, subscriptionCompletedWithoutMagicLink]);

  const renderMessage = (message: string): JSX.Element => {
    const className = `break-words ${isProRegistrationView ? 'block my-4' : ''}`;
    return (
      <>
        <SafeHTMLRender nonce={PassportNonce.RICH_TEXT} html={message} className={className} />
        <hr className='mt-4 mb-4'/>
      </>
    );
  };

  const getSubmittedMessage = (): string => {
    return registrationConfiguration?.registrationSubmittedMessage || submitMessage;
  };

  const getSubscriptionErrorMessage = (): string => {
    if (subscriptionError?.reason === 'NOT_AN_ATTENDEE') {
      return subscriptionError.message.trim() || pendingDeniedMessage;
    }
    return pendingDeniedMessage;
  };

  const shouldShowDeniedMessage = (): boolean => {
    return registrationStatus === RegistrationStatus.DENIED ||
           registrationStatus === RegistrationStatus.PENDING ||
           data.registrationApprovalEnabled ||
           (subscriptionCompletedWithoutMagicLink && (state.error !== null || state.magicLink !== null));
  };

  const setEventToken = (token: string) => {
    setToken(token);
  };

  const handleRedirect = (url: string) => {
    history.push(normalizeRoutePath(`/registration/?formName=${encodeURIComponent(url)}`, pathPrefix));
  };

  if (triageAnswerCustomFieldsError || (selectedTriageAnswer && isTriageQuestionDisabled)) {
    return <Redirect to={normalizeRoutePath(REGISTRATION_PATH, pathPrefix)} />;
  }
  return (
    <div className={`flex justify-between flex-col ${isProRegistrationView ? 'registration-container flex-row' : 'md:flex-row'}`}>
      { isProRegistrationView ? <EventDetails details={data}/> : <EventPublicInfo data={data}/> }
      <div className={clName}>
        { isProRegistrationView && <h1 className='registration-container__header h1-font-size leading-10 break-words'>
          { showSubmitMessage ? 'Sign In' : (registrationConfiguration?.registrationHeader || 'Register') }
        </h1> }
        <div
          className={clNameFormWrap}
        >
          {showSubmitMessage && !showApprovalWaitingMessage && !subscriptionInProgress && (() => {
            if (showSubscriptionError) {
              if (subscriptionError?.reason === 'Approved') {
                return renderMessage(subscriptionError?.message || submitMessage);
              }
              return renderMessage(getSubscriptionErrorMessage());
            }

            if (subscriptionInProgress) {
              return renderMessage(getSubmittedMessage());
            }

            if (shouldShowDeniedMessage()) {
              return renderMessage(pendingDeniedMessage);
            }

            return renderMessage(getSubmittedMessage());
          })()}
          {
            showErrorRes && !showSubmitMessage && !subscriptionInProgress &&
              <>
                <span>{errorMessage}</span>
                <hr className='mt-4 mb-4'/>
              </>
          }
          {
            showSubmitMessage && registrationConfiguration?.calendarButtonEnabled && !subscriptionInProgress
              ? (
                <div className='flex w-full'>
                  <Buttons.OptionsButton
                    data-test-id="add-to-calendar-invite"
                    label={'+ Add to Calendar'}
                    name={'addToCalendar'}
                    disabled={false}
                    eventId={data.eventId.toString() || ''}
                    eventName={data.name || ''}
                    className='select-box--button-small'
                    downloadIcsFile={()=> fetchOutlookCalendarForEvent(data.eventId.toString() || '', fileName, eventToken)}
                    openGoogleCalendar={()=> fetchGoogleCalendarForEvent(data.eventId.toString() || '',false, eventToken)}
                  />
                  <hr className='mt-4 mb-4'/>
                </div>
              )
              : null
          }
          {
            (showApprovalWaitingMessage || subscriptionInProgress) &&
              <>
                {registrationConfiguration?.registrationSubmittedMessage
                  ? (
                    <SafeHTMLRender
                      nonce={PassportNonce.RICH_TEXT}
                      html={registrationConfiguration.registrationSubmittedMessage}
                      className={`break-words ${isProRegistrationView ? 'block my-4' : ''}`}
                    />
                  )
                  : (
                    <span>{defaultApprovalWaitingMessage}</span>
                  )}
                <hr className='mt-4 mb-4'/>
              </>
          }
          <DefaultSpinner visible={subscriptionInProgress} />
          {loading && showRegistrationForm && !showErrorRes && !subscriptionInProgress &&
            <RegistrationForm
              key={Math.random()}
              autoSignInEnabled={registrationConfiguration?.autoSignInEnabled}
              eventId={data.eventId}
              registrationHeader={registrationConfiguration?.registrationHeader}
              registrationInstructions={registrationConfiguration?.registrationInstructions}
              customFields={formFields || []
              }
              setErrorRes={setErrorRes}
              showSubmitMessage={setShowSubmitMessage}
              toggleRegistrationForm={() => setRegistrationForm(!showRegistrationForm)}
              setEmailText={setEmailText}
              registrationSubscription={subscribe}
              setEventToken={setEventToken}
              location={location}
              isProRegistrationView={isProRegistrationView}
              generalSessionList={generalSessionList || []}
              eventTimeZone={data.timeZone}
              publicDescription={data.publicDescription}
              handleRedirect={handleRedirect}
              selectedTriageAnswer={selectedTriageAnswer}
              shouldRenderRegisterButton={!!selectedTriageAnswer || isTriageQuestionDisabled}
              showSpinner={fetching}
              registrationApprovalEnabled={data.registrationApprovalEnabled}
              showApprovalWaitingMessage={setShowApprovalWaitingMessage}
              urlParams={urlParams}
              setRegistrationStatus={setRegistrationStatus}
            />
          }
          {showSubmitMessage && !subscriptionInProgress &&
            <LoginForm
              signIn={signIn}
              errorMsg={data.loginErrorMessage}
              hideSubmitMessage={() => history.push(normalizeRoutePath(ROOT, pathPrefix))}
              showSubmitMessage={showSubmitMessage}
              emailText={emailText}
              privacyPolicyEnabled={data.privacyPolicyEnabled}
              privacyPolicyLabel={data.privacyPolicyLabel}
              isProRegistrationView={isProRegistrationView}
            />
          }
        </div>
        {!subscriptionInProgress && <RegisterBlock type={FormFooterBlockType.signIn} isProRegistrationView={isProRegistrationView} />}
      </div>
    </div>
  );
};

export default RegistrationWrapper;
